import React from 'react';
import { Navigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { FullStory } from '@fullstory/browser';
import HomeAppBar from '../../components/home/HomeAppBar';
import HomeNavBar from '../../components/home/HomeNavBar';
import HomeRouting from '../../routing/HomeRouting';
import HomeFooterBar from '../../components/home/HomeFooterBar';
import HomeSnackbar from '../../components/home/HomeSnackbar';
import { Box, CircularProgress } from '@mui/material';
import { useAppSelector } from '../../hooks/useAppSelector';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { webSocketService } from '../../services/webSocketService';
import * as userSlice from '../../data/user.slice';
import * as providerSlice from '../../data/provider.slice';
import * as securitySlice from '../../data/security.slice';
import * as lookupSlice from '../../data/lookup.slice';
import authService from '../../services/authService';

const PREFIX = 'Home';

const classes = {
  container: `${PREFIX}-container`,
  content: `${PREFIX}-content`,
  scroller: `${PREFIX}-scroller`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    flex: '0 1 100%',
  },

  [`& .${classes.content}`]: {
    display: 'flex',
    flexGrow: 1,
    paddingBottom: 0,
    backgroundColor: theme.palette.background.paper,
    // backgroundImage: 'url(/bggray.png)',
    backgroundRepeatX: 'repeat',
    backgroundRepeatY: 'no-repeat',
    overflowX: 'hidden',
    overflowY: 'scroll',
  },

  [`& .${classes.scroller}`]: {
    flexGrow: 1,
    paddingBottom: 20,
    padding: theme.spacing(3),
  },
}));

export default function Home() {
  const dispatch = useAppDispatch();
  const loadingLoggedInUser = useAppSelector(
    userSlice.selectLoadingLoggedInUserInfo,
  );
  const loadedLoggedInUser = useAppSelector(
    userSlice.selectLoadedLoggedInUserInfo,
  );
  const loggedInUserNoAccess = useAppSelector(
    userSlice.selectLoggedInUserNoAccess,
  );
  const userInfo = useAppSelector(userSlice.selectLoggedInUserInfo);

  React.useEffect(() => {
    if (loadedLoggedInUser) {
      dispatch(lookupSlice.fetchLookupItemsAsync('timeZone'));
      dispatch(lookupSlice.fetchLookupItemsAsync('tripMode'));
      dispatch(lookupSlice.fetchLookupItemsAsync('passengerCareLevel'));
      dispatch(lookupSlice.fetchLookupItemsAsync('tripLegPassengerNoteType'));
      dispatch(lookupSlice.fetchLookupItemsAsync('tripLegPassengerNoteType'));
      dispatch(lookupSlice.fetchLookupItemsAsync('tripLegDriverNoteType'));
      dispatch(lookupSlice.fetchLookupItemsAsync('alternateContactType'));
      dispatch(lookupSlice.fetchLookupItemsAsync('stateCodeList'));
      dispatch(lookupSlice.fetchLookupItemsAsync('clientVertical'));
      dispatch(lookupSlice.fetchLookupItemsAsync('clientContactDepartment'));
      dispatch(lookupSlice.fetchLookupItemsAsync('clientContactRole'));
      dispatch(lookupSlice.fetchLookupItemsAsync('clientAddressUse'));
      dispatch(lookupSlice.fetchLookupItemsAsync('tripLegOverrideReason'));
      dispatch(lookupSlice.fetchLookupItemsAsync('tripLegStatusReason'));
      dispatch(lookupSlice.fetchLookupItemsAsync('purposeCodeCategory'));
      dispatch(lookupSlice.fetchLookupItemsAsync('clientSettingOption'));
      dispatch(lookupSlice.fetchLookupItemsAsync('applicationSetting'));
      dispatch(lookupSlice.fetchLookupItemsAsync('tripLegException'));
      dispatch(lookupSlice.fetchLookupItemsAsync('discussionAcceptReason'));
      dispatch(lookupSlice.fetchLookupItemsAsync('discussionRejectReason'));
      dispatch(lookupSlice.fetchLookupItemsAsync('discussionRejectedReasons'));
      dispatch(userSlice.fetchUserApiInfoAsync());
      dispatch(lookupSlice.fetchLookupsApiInfoAsync());
      dispatch(lookupSlice.fetchTransportationAttributeMapAsync());
      dispatch(providerSlice.fetchTransportApiInfoAsync());

      webSocketService.connect(dispatch);
      authService.getTokens().subscribe((tokens) => {
        webSocketService.setSessionInfo({
          accessToken: tokens.accessToken,
          accessTokenID: tokens.idToken,
        });
      });

      if (userInfo?.userAccountID) {
        webSocketService.setSessionInfo({
          userAccountID: userInfo.userAccountID,
        });
      }

      return () => {
        webSocketService.disconnect();
      };
    }
  }, [loadedLoggedInUser]);

  React.useEffect(() => {
    if (userInfo?.emailAddress) {
      FullStory('setProperties', {
        type: 'user',
        properties: {
          email: userInfo.emailAddress,
        },
      });
    }
  }, [userInfo?.emailAddress]);

  if (loggedInUserNoAccess) {
    return <Navigate to="/noaccess" replace />;
  }

  if (!loadedLoggedInUser) {
    return (
      <Root className={classes.container}>
        <Box className={classes.content}>
          {loadingLoggedInUser && (
            <CircularProgress size={100} sx={{ margin: 'auto' }} />
          )}
        </Box>
      </Root>
    );
  }

  return (
    <Root className={classes.container}>
      <>
        <HomeAppBar />
        <HomeSnackbar />
        <Box className={classes.content}>
          <>
            <HomeNavBar />
            <Box className={classes.scroller}>
              <HomeRouting />
            </Box>
          </>
        </Box>
        <HomeFooterBar />
      </>
    </Root>
  );
}
